/* tslint:disable:max-file-line-count */
import {
  adaptDataAttributes,
  BadgeAdapter,
  getDisclaimerViewModel,
  getGalleryModel,
  getHotelFlags,
  getLocationMessaging,
  getPinnedHotelViewModel,
  getPriceModel,
  getPrimaryCardMessages,
  getPropertyKey,
  getRecentlyViewed,
  getReviewsModel,
  getShortList,
  getStarRating,
  getTravelAd,
  mapListingFooter,
  mapPropertySearchResults,
  SearchMigrationAdapter
} from 'src/common/stores/adapters/bex-api';

import {
  DataAttributes,
  DisclaimerViewModel,
  GalleryViewModel,
  HotelFlags,
  HotelResultsViewModel,
  HotelViewModel,
  ListingFooterViewModel,
  PinnedHotelViewModel,
  PriceViewModel,
  PrimaryCardMessagingModel,
  PropertyListingBadgeModel,
  RecentlyViewedModel,
  ShortlistModel,
  TravelAd
} from 'models';

import { OperationResult } from '__generated__/api/api-operations';
import { PropertyMapMigration } from '__generated__/api';

export const adaptProperties: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>
): HotelResultsViewModel => {
  const { data } = bexApiResults;
  const { propertySearch } = data;

  if (!propertySearch || !propertySearch.properties) {
    return bffResults;
  }

  return {
    ...bffResults,
    hotels: mapPropertySearchResults(data)
  };
};

export const adaptReviews = (property: PropertyMapMigration.Properties, hotel: HotelViewModel): HotelViewModel => ({
  ...hotel,
  reviews: property && property.reviews ? getReviewsModel(property.reviews) : hotel.reviews
});

// Wave 2 is the property gallery
export const adaptPropertiesWave2: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => adaptPropertyField(bffResults, bexApiResults, getGallery, bexApiPropertyDict);

const getGallery = (property: PropertyMapMigration.Properties, bffHotel: HotelViewModel): { gallery: GalleryViewModel } => ({
  gallery: property && property.propertyGallery ? getGalleryModel(property.propertyGallery) : bffHotel.gallery
});

// Wave 3 is the property price
export const adaptPropertiesWave3: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => adaptPropertyField(bffResults, bexApiResults, getPrice, bexApiPropertyDict);

const getPrice = (property: PropertyMapMigration.Properties, bffHotel: HotelViewModel): { price: PriceViewModel } => ({
  price: property && property.price ? getPriceModel(property) : bffHotel.price
});

// Wave 4 is the infosite url
export const adaptPropertiesWave4: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => adaptPropertyField(bffResults, bexApiResults, getInfositeUrl, bexApiPropertyDict);

const getInfositeUrl = (property: PropertyMapMigration.Properties, bffHotel: HotelViewModel): { infositeUrl: string } => ({
  infositeUrl: property && property.infositeURL ? property.infositeURL : bffHotel.infositeUrl
});

// Wave 5 is the shortlist model
export const adaptPropertiesWave5: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => adaptPropertyField(bffResults, bexApiResults, getShortlistModel, bexApiPropertyDict);

const getShortlistModel = (
  property: PropertyMapMigration.Properties,
  bffHotel: HotelViewModel
): {
  shortlistModel: ShortlistModel;
} => ({
  shortlistModel: property && property.shortList ? getShortList(property) : bffHotel.shortlistModel
});

// Wave 6 is for PropertyListingBadge, PropertyFlags, RecentlyViewed model,PinnedViewModel
export const adaptPropertiesWave6: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => {
  const { data } = bexApiResults;
  const { propertySearch } = data;

  if (!propertySearch || !propertySearch.properties) {
    return bffResults;
  }

  const hotels = bffResults.hotels.map(hotel => {
    const bexApiHotel = bexApiPropertyDict[getPropertyKey(String(hotel.id), hotel.travelAd)];

    return {
      ...hotel,
      ...getPropertyFlags(bexApiHotel, hotel),
      ...getRecentlyViewedModel(bexApiHotel, hotel),
      ...getPinnedHotelViewedModel(bexApiHotel, hotel),
      ...getDataAttributes(bexApiHotel, hotel),
      ...getSupportingMessages(bexApiHotel, hotel)
    };
  });

  return {
    ...bffResults,
    hotels
  };
};

const getPropertyFlags = (
  property: PropertyMapMigration.Properties,
  bffHotel: HotelViewModel
): {
  hotelFlags: HotelFlags;
} => ({
  hotelFlags: property && property.availability ? getHotelFlags(property) : bffHotel.hotelFlags
});

const getPinnedHotelViewedModel = (
  property: PropertyMapMigration.Properties,
  bffHotel: HotelViewModel
): {
  pinnedDetails: PinnedHotelViewModel;
} => ({
  pinnedDetails: property && property.pinnedDetails ? getPinnedHotelViewModel(property) : bffHotel.pinnedDetails
});

const getDataAttributes = (
  property: PropertyMapMigration.Properties,
  bffHotel: HotelViewModel
): {
  dataAttributes: DataAttributes;
} => ({
  dataAttributes: property && property.dataAttributes ? adaptDataAttributes(property) : bffHotel.dataAttributes
});

const getRecentlyViewedModel = (
  property: PropertyMapMigration.Properties,
  bffHotel: HotelViewModel
): {
  recentlyViewed: RecentlyViewedModel;
} => ({
  recentlyViewed: property && property.recentlyViewed ? getRecentlyViewed(property) : bffHotel.recentlyViewed
});

const getSupportingMessages = (
  property: PropertyMapMigration.Properties,
  bffHotel: HotelViewModel
): {
  supportingMessages: string[];
} => ({
  supportingMessages:
    property && property.supportingMessages && property.supportingMessages.length > 0
      ? property.supportingMessages
      : bffHotel.supportingMessages
});

// Wave 6.5 is for PropertyListingBadge, DRR
export const adaptPropertiesWave6_5: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => {
  const { data } = bexApiResults;
  const { propertySearch } = data;

  if (!propertySearch || !propertySearch.properties) {
    return bffResults;
  }

  const hotels = bffResults.hotels.map(hotel => {
    const bexApiHotel = bexApiPropertyDict[getPropertyKey(String(hotel.id), hotel.travelAd)];

    return {
      ...hotel,
      ...getPropertyListingBadgeModel(bexApiHotel, hotel)
    };
  });

  return {
    ...bffResults,
    hotels
  };
};

const getPropertyListingBadgeModel = (
  property: PropertyMapMigration.Properties,
  bffHotel: HotelViewModel
): {
  propertyListingBadgeModel: PropertyListingBadgeModel;
} => ({
  propertyListingBadgeModel:
    property && property.offerBadge ? BadgeAdapter.getPropertyListingBadgeModel(property) : bffHotel.propertyListingBadgeModel
});

// Wave 7 is primaryCardMessages
export const adaptPropertiesWave7: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => {
  const { data } = bexApiResults;
  const { propertySearch } = data;

  if (!propertySearch || !propertySearch.properties) {
    return bffResults;
  }

  const hotels = bffResults.hotels.map(hotel => {
    const bexApiHotel = bexApiPropertyDict[getPropertyKey(String(hotel.id), hotel.travelAd)];

    return {
      ...hotel,
      ...adaptPrimaryCardMessages(bexApiHotel, hotel),
      ...adaptDisclaimerViewModel(bexApiHotel, hotel)
    };
  });

  return {
    ...bffResults,
    hotels
  };
};

const adaptPrimaryCardMessages = (
  property: PropertyMapMigration.Properties,
  bffHotel: HotelViewModel
): { primaryCardMessages: PrimaryCardMessagingModel[] } => ({
  primaryCardMessages:
    property && property.offerSummary && property.offerSummary.messages && property.offerSummary.messages.length > 0
      ? getPrimaryCardMessages(property)
      : bffHotel.primaryCardMessages
});

const adaptDisclaimerViewModel = (
  property: PropertyMapMigration.Properties,
  bffHotel: HotelViewModel
): { disclaimerDialog: DisclaimerViewModel } => ({
  disclaimerDialog: property && property.legalDisclaimer ? getDisclaimerViewModel(property) : bffHotel.disclaimerDialog
});

// Wave 8 is the property star
export const adaptPropertiesWave8: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => {
  const { data } = bexApiResults;
  const { propertySearch } = data;

  if (!propertySearch || !propertySearch.properties) {
    return bffResults;
  }

  const hotels = bffResults.hotels.map(hotel => {
    const bexApiHotel = bexApiPropertyDict[String(hotel.id)];

    return {
      ...hotel,
      ...adaptStar(bexApiHotel, hotel),
      ...adaptStarRatingIcon(bexApiHotel, hotel)
    };
  });

  return {
    ...bffResults,
    hotels
  };
};

const adaptStar = (property: PropertyMapMigration.Properties, bffHotel: HotelViewModel): { stars: number } => ({
  stars: property ? getStarRating(property) : bffHotel.stars
});

const adaptStarRatingIcon = (property: PropertyMapMigration.Properties, bffHotel: HotelViewModel): { starRatingIcon: string } => ({
  starRatingIcon: property && property.starRatingIcon ? property.starRatingIcon : bffHotel.starRatingIcon
});

// Wave 9 is the property name, neighborhood, and property summary
export const adaptPropertiesWave9: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => {
  const { data } = bexApiResults;
  const { propertySearch } = data;

  if (!propertySearch || !propertySearch.properties) {
    return bffResults;
  }

  const hotels = bffResults.hotels.map(hotel => {
    const bexApiHotel = bexApiPropertyDict[String(hotel.id)];

    return {
      ...hotel,
      ...adaptPropertyName(bexApiHotel, hotel),
      ...adaptNeighborhood(bexApiHotel, hotel),
      ...adaptPropertySummary(bexApiHotel, hotel)
    };
  });

  return {
    ...bffResults,
    hotels
  };
};

const adaptPropertyName = (property: PropertyMapMigration.Properties, bffHotel: HotelViewModel): { hotelName: string; name: string } => ({
  hotelName: property && property.name ? property.name : bffHotel.hotelName,
  name: property && property.name ? property.name : bffHotel.name
});

const adaptNeighborhood = (property: PropertyMapMigration.Properties, bffHotel: HotelViewModel): { neighborhood: string } => ({
  neighborhood: property ? getLocationMessaging(property) : bffHotel.neighborhood
});

const adaptPropertySummary = (property: PropertyMapMigration.Properties, bffHotel: HotelViewModel): { propertySummary: string } => ({
  propertySummary: property && property.highlightedPropertyDetail ? property.highlightedPropertyDetail : bffHotel.propertySummary
});

// Wave 10 is travelAds and listing footer (will be added later)
export const adaptPropertiesWave10: SearchMigrationAdapter = (
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => {
  const { data } = bexApiResults;
  const { propertySearch } = data;

  if (!propertySearch || !propertySearch.properties) {
    return bffResults;
  }

  const hotels = bffResults.hotels.map(hotel => {
    const bexApiHotel = bexApiPropertyDict[getPropertyKey(String(hotel.id), hotel.travelAd)];

    return {
      ...hotel,
      ...adaptTravelAd(bexApiHotel, hotel),
      ...adaptListingFooter(bexApiHotel, hotel)
    };
  });

  return {
    ...bffResults,
    hotels
  };
};

const adaptTravelAd = (property: PropertyMapMigration.Properties, bffHotel: HotelViewModel): { travelAd: TravelAd } => ({
  travelAd: property && property.sponsoredListing ? getTravelAd(property) : bffHotel.travelAd
});

const adaptListingFooter = (
  property: PropertyMapMigration.Properties,
  bffHotel: HotelViewModel
): { listingFooter: ListingFooterViewModel } => ({
  listingFooter: property && property.listingFooter ? mapListingFooter(property) : bffHotel.listingFooter
});

const adaptPropertyField = <T extends keyof HotelViewModel>(
  bffResults: HotelResultsViewModel,
  bexApiResults: OperationResult<PropertyMapMigration.Query>,
  fieldToMigrate: (bex: PropertyMapMigration.Properties, bff: HotelViewModel) => HotelViewModel[T],
  bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties }
): HotelResultsViewModel => {
  const { data } = bexApiResults;
  const { propertySearch } = data;

  if (!propertySearch || !propertySearch.properties) {
    return bffResults;
  }

  const hotels = bffResults.hotels.map(hotel => {
    const bexApiHotel = bexApiPropertyDict[getPropertyKey(String(hotel.id), hotel.travelAd)];

    const field = fieldToMigrate(bexApiHotel, hotel);

    return {
      ...hotel,
      ...field
    };
  });

  return {
    ...bffResults,
    hotels
  };
};
